import ufeApi from 'services/api/ufeApi';
import { gqlClient } from 'services/api/gql/gqlClient';
import { GetNcrOrderDetails } from 'constants/gql/queries/checkout/ncrOrderDetails.gql';
import { OrderDetails } from 'constants/gql/queries/checkout/orderDetails.gql';
import Location from 'utils/Location';
import UrlUtils from 'utils/Url';
import OrderUtils from 'utils/Order';

// https://confluence.sephora.com/wiki/display/ILLUMINATE/Get+Order+Details+API
// https://confluence.sephora.com/wiki/display/ILLUMINATE/NCR+Order+Details+API

const DEFAULT_BASKET_TYPE = 'ShipToHome';

// OIS returns sku.displayName; downstream components read sku.productName — backfill when absent
const normalizeItems = items =>
    (items || []).map(item => ({
        ...item,
        sku: { ...item.sku, productName: item.sku?.productName ?? item.sku?.displayName }
    }));

const MDM_DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

// Converts an MDM 24-hour time ("20:00") to the OXS display format ("08:00PM") the store-hours UI expects.
const toTwelveHourTime = time => {
    const [hour, minute] = (time || '').split(':');
    const parsedHour = Number(hour);

    if (!minute || Number.isNaN(parsedHour)) {
        return '';
    }

    const period = parsedHour >= 12 ? 'PM' : 'AM';
    const twelveHour = parsedHour % 12 === 0 ? 12 : parsedHour % 12;

    return `${String(twelveHour).padStart(2, '0')}:${minute}${period}`;
};

// Maps a single MDM day ({ open, close }) to the OXS "open-close" string, or '' when closed/missing.
const toDayHours = day => (day?.open && day?.close ? `${toTwelveHourTime(day.open)}-${toTwelveHourTime(day.close)}` : '');

// OIS pickup.storeDetails is an MDMLocation (nested general/omni/analyticalParameters); the order
// details components read a flat OXS-style storeDetails. Flatten the MDM shape into that here:
// general.primaryAddress → address, general.telecoms[PHONE_1] → address.phone,
// general.regularHours → storeHours (OXS day strings), omni → curbside/bopis flags.
const normalizeStoreDetails = (storeDetails, pickupStore) => {
    if (!storeDetails) {
        return storeDetails;
    }

    const general = storeDetails.general || {};
    const { primaryAddress, regularHours } = general;
    const phone = general.telecoms?.find(telecom => telecom?.type === 'PHONE_1')?.data;
    const storeName = storeDetails.storeName ?? general.storeName ?? pickupStore;

    return {
        ...storeDetails,
        storeName,
        displayName: storeDetails.displayName ?? general.storeName ?? pickupStore,
        storeType: storeDetails.storeType ?? storeDetails.analyticalParameters?.storeType,
        isBopisable: storeDetails.isBopisable ?? storeDetails.omni?.bopisAvailable,
        isCurbsideEnabled: storeDetails.isCurbsideEnabled ?? storeDetails.omni?.curbsideAvailable,
        ...(primaryAddress && {
            address: {
                address1: primaryAddress.streetAddress,
                address2: primaryAddress.suite,
                city: primaryAddress.city,
                state: primaryAddress.state,
                postalCode: primaryAddress.postalCode,
                phone
            }
        }),
        ...(regularHours && {
            storeHours: {
                timeZone: general.timezone,
                ...MDM_DAY_KEYS.reduce((acc, day) => ({ ...acc, [`${day}Hours`]: toDayHours(regularHours[day]) }), {})
            }
        })
    };
};

// OIS returns the pickup person as `firstName`; the page reads `pickup.firstname`. The store name is
// available both as the flat `pickup.pickupStore` string and via the MDM storeDetails (flattened above).
const normalizePickup = pickup =>
    pickup
        ? {
            ...pickup,
            firstname: pickup.firstname ?? pickup.firstName,
            storeDetails: normalizeStoreDetails(pickup.storeDetails, pickup.pickupStore)
        }
        : pickup;

// Normalizes an OIS orderDetails/ncrOrderDetails result to match the OXS shape expected by
// downstream Order.js utilities: maps known OIS shippingGroupType values to their PascalCase
// equivalents, backfills productName, and derives items.items / items.itemsByBasket from shippingGroups.
const normalizeOisOrderDetails = result => {
    const { SHIPPING_GROUPS } = OrderUtils;

    const OIS_TO_UFE_GROUP_TYPE = {
        HARD_GOOD_SHIPPING_GROUP: SHIPPING_GROUPS.HARD_GOOD,
        GIFT_CARD_SHIPPING_GROUP: SHIPPING_GROUPS.GIFT,
        ELECTRONIC_SHIPPING_GROUP: SHIPPING_GROUPS.ELECTRONIC,
        SAME_DAY_SHIPPING_GROUP: SHIPPING_GROUPS.SAME_DAY,
        SDU_ELECTRONIC_SHIPPING_GROUP: SHIPPING_GROUPS.SDU_ELECTRONIC
    };

    const SHIPPING_GROUP_TO_BASKET_TYPE = {
        [SHIPPING_GROUPS.SAME_DAY]: 'SameDay'
    };

    const entries = result.shippingGroups?.shippingGroupsEntries || [];

    const normalizedEntries = entries.map(entry => {
        const groupType = OIS_TO_UFE_GROUP_TYPE[entry.shippingGroupType] || entry.shippingGroupType || SHIPPING_GROUPS.HARD_GOOD;

        return {
            ...entry,
            shippingGroupType: groupType,
            shippingGroup: {
                ...entry.shippingGroup,
                shippingGroupType: groupType,
                items: normalizeItems(entry.shippingGroup?.items)
            }
        };
    });

    const allItems = normalizedEntries.flatMap(entry => entry.shippingGroup?.items || []);

    const itemsByBasket = Object.values(
        normalizedEntries.reduce(
            (acc, entry) => {
                const basketType = SHIPPING_GROUP_TO_BASKET_TYPE[entry.shippingGroupType] || DEFAULT_BASKET_TYPE;

                if (!acc[basketType]) {
                    acc[basketType] = { basketType, items: [] };
                }

                acc[basketType].items.push(...(entry.shippingGroup?.items || []));

                return acc;
            },
            { [DEFAULT_BASKET_TYPE]: { basketType: DEFAULT_BASKET_TYPE, items: [] } }
        )
    );

    return {
        ...result,
        items: { ...result.items, items: allItems, itemsByBasket },
        pickup: normalizePickup(result.pickup),
        shippingGroups: {
            ...result.shippingGroups,
            shippingGroupsEntries: normalizedEntries
        }
    };
};

/**
 * Fetches order details for the given order ID.
 *
 * Routing priority:
 * 1. NCR replacement orders (isReshipOrder + global + returns flags) → GetNcrOrderDetails GQL
 * 2. Standard order details page (global + orderDetails flags + isOrderDetailsPage) → OrderDetails GQL
 * 3. All other cases → OXS REST fallback
 *
 * OIS responses are normalized to match the OXS shape expected by downstream Order.js utilities.
 *
 * @param {string} orderId - The order ID to fetch details for
 * @param {(string|string[])} [guestEmail=''] - Guest email for unauthenticated order lookups. Accepts an
 *     array (as returned by UrlUtils.getParams()) which is normalized to the first value.
 * @param {boolean} [isReshipOrder=false] - Whether this is a replacement/reship order
 * @param {boolean} [appendRequestOrigin=false] - Whether to append requestOrigin based on page context
 * @param {string} [originalOrderId=''] - Original order ID for NCR reship contexts
 * @returns {Promise<Object>} Order details object
 * @throws {Error} Throws when OIS response shape is unexpected
 */
const getOrderDetails = async (orderId, guestEmail = '', isReshipOrder = false, appendRequestOrigin = false, originalOrderId = '') => {
    const globalEnabled = Sephora?.configurationSettings?.oisMigration?.global?.isEnabled;
    const returnsEnabled = Sephora?.configurationSettings?.oisMigration?.returns?.isEnabled;
    const orderDetailsEnabled = Sephora?.configurationSettings?.oisMigration?.orderDetails?.isEnabled;
    const isOrderDetailsPage = Location.isOrderDetailsPage();

    const normalizedGuestEmail = Array.isArray(guestEmail) ? guestEmail[0] : guestEmail;

    const { requestOrigin } = UrlUtils.getParams();
    const resolvedOrigin =
        isOrderDetailsPage || appendRequestOrigin
            ? requestOrigin?.includes?.(OrderUtils.ORDER_DETAILS_REQUESTS_ORIGIN.ORD_CONFIRMATION_PAGE)
                ? OrderUtils.ORDER_DETAILS_REQUESTS_ORIGIN.ORD_CONFIRMATION_PAGE
                : OrderUtils.ORDER_DETAILS_REQUESTS_ORIGIN.ORD_DETAILS_PAGE
            : '';

    // NCR replacement order path
    if (isReshipOrder && globalEnabled && returnsEnabled) {
        const data = await gqlClient.query({
            operationName: 'GetNcrOrderDetails',
            query: GetNcrOrderDetails,
            variables: {
                input: {
                    orderId,
                    isReshipOrder: true,
                    ...(originalOrderId && { originalOrderId }),
                    ...(resolvedOrigin && { requestOrigin: resolvedOrigin })
                }
            }
        });

        const result = data?.ncrOrderDetails;

        if (!result) {
            throw new Error('Get NCR order details: unexpected response shape');
        }

        return normalizeOisOrderDetails(result);
    }

    // Standard order details OIS path — only for the order details page, not checkout.
    // OIS OrderDetailsInput accepts an optional guestEmail for guest order validation,
    // so guest orders are routed through OIS as well.
    if (globalEnabled && orderDetailsEnabled && isOrderDetailsPage) {
        const data = await gqlClient.query({
            operationName: 'OrderDetails',
            query: OrderDetails,
            variables: { input: { orderId, ...(normalizedGuestEmail && { guestEmail: normalizedGuestEmail }) } }
        });

        const result = data?.orderDetails;

        if (!result) {
            throw new Error('Get order details: unexpected response shape');
        }

        return normalizeOisOrderDetails(result);
    }

    // OXS REST fallback path
    const url = [
        `/api/checkout/orders/${orderId}?includeShippingItems=true&includeProfileFlags=true`,
        originalOrderId ? `&originalOrderId=${originalOrderId}` : '',
        normalizedGuestEmail ? `&guestEmail=${encodeURIComponent(normalizedGuestEmail)}` : '',
        resolvedOrigin ? `&requestOrigin=${resolvedOrigin}` : '',
        isReshipOrder ? `&isReshipOrder=${isReshipOrder}` : ''
    ].join('');

    return ufeApi.makeRequest(url, { method: 'GET' });
};

export default getOrderDetails;
